import { uuid, Uuid } from '@softline/core';
import { ConditionFilter, Filter } from '../types/filter';

/**
 * Pure, immutable helpers for the custom filter builder. The builder always
 * works on a ConditionFilter root (ensureRootGroup); simplifyFilterTree
 * unwraps that editing shape back into the persisted form on apply.
 */

export function ensureRootGroup<T extends object>(
  filter: Filter<T> | null
): ConditionFilter<T> {
  if (filter?.type === 'conditions') return filter;
  return {
    uuid: uuid(),
    type: 'conditions',
    operator: 'AND',
    filters: filter ? [filter] : [],
  };
}

/** Drops empty groups and collapses single-child groups, bottom-up. */
export function simplifyFilterTree<T extends object>(
  filter: Filter<T> | null
): Filter<T> | null {
  if (!filter) return null;
  if (filter.type !== 'conditions') return filter;
  const filters = filter.filters
    .map((o) => simplifyFilterTree(o))
    .filter((o): o is Filter<T> => o !== null);
  if (filters.length === 0) return null;
  if (filters.length === 1) return filters[0];
  return { ...filter, filters };
}

export function addNodeToGroup<T extends object>(
  tree: ConditionFilter<T>,
  groupUuid: Uuid,
  node: Filter<T>
): ConditionFilter<T> {
  return mapGroups(tree, (group) =>
    group.uuid === groupUuid ? { ...group, filters: [...group.filters, node] } : group
  );
}

/** Removes a node while editing — empty groups are kept so the user can refill them. */
export function removeNodeFromTree<T extends object>(
  tree: ConditionFilter<T>,
  nodeUuid: Uuid
): ConditionFilter<T> {
  return mapGroups(tree, (group) => ({
    ...group,
    filters: group.filters.filter((o) => o.uuid !== nodeUuid),
  }));
}

export function replaceNodeInTree<T extends object>(
  tree: ConditionFilter<T>,
  nodeUuid: Uuid,
  replacement: Filter<T>
): ConditionFilter<T> {
  if (tree.uuid === nodeUuid && replacement.type === 'conditions') return replacement;
  return mapGroups(tree, (group) => ({
    ...group,
    filters: group.filters.map((o) => (o.uuid === nodeUuid ? replacement : o)),
  }));
}

export function toggleGroupOperator<T extends object>(
  tree: ConditionFilter<T>,
  groupUuid: Uuid,
  operator: 'AND' | 'OR'
): ConditionFilter<T> {
  return mapGroups(tree, (group) =>
    group.uuid === groupUuid ? { ...group, operator } : group
  );
}

function mapGroups<T extends object>(
  node: ConditionFilter<T>,
  fn: (group: ConditionFilter<T>) => ConditionFilter<T>
): ConditionFilter<T> {
  const mapped = fn(node);
  return {
    ...mapped,
    filters: mapped.filters.map((o) =>
      o.type === 'conditions' ? mapGroups(o, fn) : o
    ),
  };
}
